import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import GeneralLayout from '../../../components/GeneralLayout';
import ContentBox from '../../../components/ContentBox';
import ConfirmModal from '../../../components/ConfirmModal';
import {
    getAppreciationStaffSettings,
    updateAppreciationStaffSettings,
    getAppreciationStaffPosts,
    getAppreciationStaffStats,
    removeAppreciationStaffPost,
} from '../../../services/api';

const DATE_PRESETS = [
    { key: 'all', label: 'All time' },
    { key: '7d', label: 'Last 7 days' },
    { key: '30d', label: 'Last 30 days' },
    { key: 'month', label: 'This month' },
    { key: 'custom', label: 'Custom range' },
];

const toDateInput = (d) => d.toISOString().slice(0, 10);

const rangeForPreset = (preset) => {
    const now = new Date();
    if (preset === '7d') {
        const from = new Date(now);
        from.setDate(from.getDate() - 6);
        return { from: toDateInput(from), to: toDateInput(now) };
    }
    if (preset === '30d') {
        const from = new Date(now);
        from.setDate(from.getDate() - 29);
        return { from: toDateInput(from), to: toDateInput(now) };
    }
    if (preset === 'month') {
        const from = new Date(now.getFullYear(), now.getMonth(), 1);
        return { from: toDateInput(from), to: toDateInput(now) };
    }
    return { from: '', to: '' };
};

function StatTile({ label, value, tone = 'default' }) {
    const tones = {
        default: 'bg-gray-100 border-black/25',
        good: 'bg-green-100 border-green-700/40',
        warn: 'bg-orange-100 border-orange-700/40',
        bad: 'bg-red-100 border-red-700/40',
    };

    return (
        <div className={`border ${tones[tone] || tones.default} px-3 py-2`}>
            <div className="font-['Jersey_10'] text-[26px] leading-none">{value}</div>
            <div className="text-[10px] uppercase tracking-wide text-coal/60 mt-1">{label}</div>
        </div>
    );
}

function SettingsPanel({ settings, onSaved }) {
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (settings) setDraft({ ...settings });
    }, [settings]);

    if (!draft) {
        return <p className="text-sm text-gray-500">Loading settings...</p>;
    }

    const save = async (overrides = {}) => {
        const next = { ...draft, ...overrides };
        setSaving(true);
        try {
            const res = await updateAppreciationStaffSettings({
                enabled: next.enabled,
                monthlyLimit: Number(next.monthlyLimit),
                freeCount: Number(next.freeCount),
                cardCost: Number(next.cardCost),
            });

            if (res.success) {
                toast.success('Settings saved');
                onSaved(res.settings);
            } else {
                toast.error(res.message || 'Could not save settings');
            }
        } catch {
            toast.error('Could not reach the server');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className={`flex items-center justify-between gap-3 border p-3 ${draft.enabled ? 'bg-green-50 border-green-700/40' : 'bg-red-50 border-red-700/40'}`}>
                <div>
                    <div className="font-['Jersey_10'] uppercase text-[18px] tracking-[1px]">
                        New nominations: {draft.enabled ? 'Open' : 'Paused'}
                    </div>
                    <p className="text-[12px] text-coal/70 mt-0.5">
                        When paused, members can still read and like the wall but cannot post.
                    </p>
                </div>
                <button
                    onClick={() => { setDraft(d => ({ ...d, enabled: !d.enabled })); save({ enabled: !draft.enabled }); }}
                    disabled={saving}
                    className={`shrink-0 font-['Jersey_10'] uppercase text-[15px] tracking-[1px] px-4 py-2 text-white transition-colors disabled:opacity-50 ${draft.enabled ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
                    style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)' }}
                >
                    {draft.enabled ? 'Pause' : 'Open'}
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                    <label className="block font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-700 mb-1">
                        Nominations per month
                    </label>
                    <input
                        type="number"
                        min="1"
                        max="50"
                        value={draft.monthlyLimit}
                        onChange={(e) => setDraft(d => ({ ...d, monthlyLimit: e.target.value }))}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm"
                    />
                </div>
                <div>
                    <label className="block font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-700 mb-1">
                        Free nominations
                    </label>
                    <input
                        type="number"
                        min="0"
                        max={draft.monthlyLimit}
                        value={draft.freeCount}
                        onChange={(e) => setDraft(d => ({ ...d, freeCount: e.target.value }))}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm"
                    />
                </div>
                <div>
                    <label className="block font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-700 mb-1">
                        Cost after free (cards)
                    </label>
                    <input
                        type="number"
                        min="0"
                        max="100"
                        value={draft.cardCost}
                        onChange={(e) => setDraft(d => ({ ...d, cardCost: e.target.value }))}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm"
                    />
                </div>
            </div>

            <button
                onClick={() => save()}
                disabled={saving}
                className="w-full font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-4 py-2 bg-maroon text-white hover:bg-red-900 transition-colors rounded disabled:opacity-50"
                style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
            >
                {saving ? 'Saving...' : 'Save limits'}
            </button>
        </div>
    );
}

function StaffPostRow({ post, onRemove }) {
    const [revealed, setRevealed] = useState(!post.isAnonymous);
    const isRemoved = post.status === 'removed';

    return (
        <div className={`border border-black/25 p-3 ${isRemoved ? 'bg-red-50 opacity-70' : 'bg-white'}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap text-[12px]">
                    <span className="bg-gray-200 border border-black/30 px-2 py-[1px] uppercase tracking-wide text-[10px]">
                        To {post.toHabboName}
                    </span>

                    <span className="relative inline-flex items-center">
                        <span className="bg-gold border border-black/30 px-2 py-[1px] uppercase tracking-wide text-[10px]">
                            From {post.fromHabboName}
                        </span>
                        {!revealed && (
                            <button
                                onClick={() => setRevealed(true)}
                                title="Anonymous post, click to reveal the sender"
                                className="absolute inset-0 bg-coal text-white text-[9px] uppercase tracking-wide flex items-center justify-center border border-black/40"
                            >
                                Anon · reveal
                            </button>
                        )}
                    </span>

                    {post.cardsPaid > 0 && (
                        <span className="bg-orange-200 border border-black/30 px-2 py-[1px] uppercase tracking-wide text-[10px]">
                            {post.cardsPaid} card{post.cardsPaid !== 1 ? 's' : ''}
                        </span>
                    )}

                    <span className="bg-pink-200 border border-black/30 px-2 py-[1px] uppercase tracking-wide text-[10px]">
                        {post.likeCount} like{post.likeCount !== 1 ? 's' : ''}
                    </span>

                    {isRemoved && (
                        <span className="bg-red-300 border border-black/30 px-2 py-[1px] uppercase tracking-wide text-[10px]">
                            Removed{post.removedBy ? ` by ${post.removedBy}` : ''}
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    <span className="text-[11px] text-coal/50">
                        {new Date(post.createdAt).toLocaleString('en-GB')}
                    </span>
                    {!isRemoved && (
                        <button
                            onClick={() => onRemove(post)}
                            className="font-['Jersey_10'] uppercase text-[13px] tracking-[1px] px-2 py-1 bg-red-600 text-white hover:bg-red-700"
                            style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)' }}
                        >
                            Remove
                        </button>
                    )}
                </div>
            </div>

            <p className="text-[13px] text-coal whitespace-pre-wrap break-words">{post.message}</p>
        </div>
    );
}

function Appreciation({ userData }) {
    const [settings, setSettings] = useState(null);
    const [stats, setStats] = useState(null);
    const [posts, setPosts] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [page, setPage] = useState(1);
    const [preset, setPreset] = useState('all');
    const [range, setRange] = useState({ from: '', to: '' });
    const [includeRemoved, setIncludeRemoved] = useState(false);
    const [loading, setLoading] = useState(true);
    const [pendingRemoval, setPendingRemoval] = useState(null);

    const isStaff = Boolean(userData?.hasStaffRole);

    const loadSettings = useCallback(async () => {
        const res = await getAppreciationStaffSettings();
        if (res.success) setSettings(res.settings);
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [postsRes, statsRes] = await Promise.all([
                getAppreciationStaffPosts({ page, from: range.from, to: range.to, includeRemoved }),
                getAppreciationStaffStats({ from: range.from, to: range.to }),
            ]);

            if (postsRes.success) {
                setPosts(postsRes.posts || []);
                setPagination(postsRes.pagination || null);
            } else {
                toast.error(postsRes.message || 'Could not load posts');
            }

            if (statsRes.success) setStats(statsRes.stats);
        } catch {
            toast.error('Could not reach the server');
        } finally {
            setLoading(false);
        }
    }, [page, range.from, range.to, includeRemoved]);

    useEffect(() => {
        if (!isStaff) return;
        loadSettings().catch(() => toast.error('Could not load settings'));
    }, [isStaff, loadSettings]);

    useEffect(() => {
        if (!isStaff) return;
        loadData();
    }, [isStaff, loadData]);

    if (!userData || !userData.hasStaffRole) {
        return <Navigate to="/" replace />;
    }

    const applyPreset = (key) => {
        setPreset(key);
        setPage(1);
        if (key !== 'custom') setRange(rangeForPreset(key));
    };

    const confirmRemoval = async () => {
        const post = pendingRemoval;
        setPendingRemoval(null);
        if (!post) return;

        try {
            const res = await removeAppreciationStaffPost(post.id);
            if (res.success) {
                toast.success('Post removed from the wall');
                setPosts(prev => prev.map(p => p.id === post.id ? { ...p, status: 'removed' } : p));
                setStats(prev => prev ? { ...prev, activePosts: Math.max(0, prev.activePosts - 1), removedPosts: prev.removedPosts + 1 } : prev);
            } else {
                toast.error(res.message || 'Could not remove the post');
            }
        } catch {
            toast.error('Could not reach the server');
        }
    };

    return (
        <GeneralLayout userData={userData}>
            <ContentBox theme="default" tint="lightskyblue" header="I Appreciate You Settings">
                <SettingsPanel settings={settings} onSaved={setSettings} />
            </ContentBox>

            <ContentBox theme="default" tint="lightskyblue" header="Stats">
                <div className="flex flex-wrap items-end gap-2 mb-3">
                    <div>
                        <label className="block font-['Jersey_10'] uppercase text-[12px] tracking-[1px] text-gray-700 mb-1">
                            Period
                        </label>
                        <select
                            value={preset}
                            onChange={(e) => applyPreset(e.target.value)}
                            className="px-2 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm"
                        >
                            {DATE_PRESETS.map(p => (
                                <option key={p.key} value={p.key}>{p.label}</option>
                            ))}
                        </select>
                    </div>

                    {preset === 'custom' && (
                        <>
                            <div>
                                <label className="block font-['Jersey_10'] uppercase text-[12px] tracking-[1px] text-gray-700 mb-1">From</label>
                                <input
                                    type="date"
                                    value={range.from}
                                    onChange={(e) => { setRange(r => ({ ...r, from: e.target.value })); setPage(1); }}
                                    className="px-2 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm"
                                />
                            </div>
                            <div>
                                <label className="block font-['Jersey_10'] uppercase text-[12px] tracking-[1px] text-gray-700 mb-1">To</label>
                                <input
                                    type="date"
                                    value={range.to}
                                    onChange={(e) => { setRange(r => ({ ...r, to: e.target.value })); setPage(1); }}
                                    className="px-2 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm"
                                />
                            </div>
                        </>
                    )}

                    <label className="flex items-center gap-2 cursor-pointer select-none pb-2">
                        <input
                            type="checkbox"
                            checked={includeRemoved}
                            onChange={(e) => { setIncludeRemoved(e.target.checked); setPage(1); }}
                            className="w-4 h-4 accent-maroon"
                        />
                        <span className="text-[12px] text-coal">Show removed posts</span>
                    </label>
                </div>

                {stats ? (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                            <StatTile label="Posts in period" value={stats.totalPosts} />
                            <StatTile label="On the wall" value={stats.activePosts} tone="good" />
                            <StatTile label="Removed" value={stats.removedPosts} tone={stats.removedPosts > 0 ? 'bad' : 'default'} />
                            <StatTile label="Anonymous" value={stats.anonymousPosts} />
                            <StatTile label="Unique senders" value={stats.uniqueSenders} />
                            <StatTile label="Unique recipients" value={stats.uniqueRecipients} />
                            <StatTile label="Total likes" value={stats.totalLikes} />
                            <StatTile label="Framp cards spent" value={stats.cardsSpent} tone="warn" />
                        </div>

                        {stats.topRecipients?.length > 0 && (
                            <div className="mt-3 border border-black/20 p-2">
                                <div className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] text-coal/70 mb-1">
                                    Most appreciated
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {stats.topRecipients.map(r => (
                                        <span key={r.habboName} className="bg-gold border border-black/30 px-2 py-[2px] text-[11px]">
                                            {r.habboName} · {r.total}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <p className="text-sm text-gray-500">Loading stats...</p>
                )}
            </ContentBox>

            <ContentBox theme="default" tint="lightskyblue" header="Posts">
                {loading ? (
                    <p className="text-sm text-gray-500">Loading posts...</p>
                ) : posts.length === 0 ? (
                    <p className="text-sm text-gray-500">No posts for this period.</p>
                ) : (
                    <div className="space-y-2">
                        {posts.map(post => (
                            <StaffPostRow key={post.id} post={post} onRemove={setPendingRemoval} />
                        ))}
                    </div>
                )}

                {pagination && pagination.totalPages > 1 && (
                    <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-gray-200">
                        <span className="text-xs text-gray-500 font-['Jersey_10'] uppercase tracking-wide">
                            Page {pagination.page} of {pagination.totalPages}, {pagination.totalItems} posts
                        </span>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={!pagination.hasPrevPage}
                                className="font-['Jersey_10'] uppercase text-[15px] tracking-[1px] px-3 py-1 bg-offwhite border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                &lsaquo; Prev
                            </button>
                            <button
                                onClick={() => setPage(p => p + 1)}
                                disabled={!pagination.hasNextPage}
                                className="font-['Jersey_10'] uppercase text-[15px] tracking-[1px] px-3 py-1 bg-offwhite border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Next &rsaquo;
                            </button>
                        </div>
                    </div>
                )}
            </ContentBox>

            <ConfirmModal
                isOpen={Boolean(pendingRemoval)}
                onClose={() => setPendingRemoval(null)}
                onConfirm={confirmRemoval}
                title="Remove this post?"
                message={pendingRemoval
                    ? `Remove the post for ${pendingRemoval.toHabboName} from ${pendingRemoval.fromHabboName}? It will disappear from the wall immediately. This does not refund their nomination or framp cards.`
                    : ''}
            />
        </GeneralLayout>
    );
}

export default Appreciation;
